import React, { useMemo } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import {
  getKenyaCountyCensusSnapshot,
  getKenyaCountyFacilityAccessSnapshot,
  getKenyaCountyGoogleAdsDisplayName,
  getKenyaCountyKdhs2022Snapshot,
  KENYA_COUNTY_CENSUS_VINTAGE_LABEL,
  KENYA_FACILITY_ACCESS_SOURCE_URL,
  KENYA_FACILITY_ACCESS_VINTAGE_LABEL,
  KENYA_GOOGLE_ADS_COUNTY_LABEL_NOTE,
  KENYA_KDHS_2022_DATA_TYPE_NOTE,
  KENYA_KDHS_2022_PROGRAM_URL,
  KENYA_KDHS_2022_VINTAGE_LABEL,
} from "../../services/census/getKenyaCountyCensusSnapshot";

function fmtCount(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return Number(n).toLocaleString();
}

function fmtPct(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return `${Number(n).toFixed(1)}%`;
}

function fmtDecimal(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

/** County vs national (KDHS second column on fact sheet). */
function fmtCountyNat(c, n) {
  if (c == null || !Number.isFinite(Number(c))) return "—";
  if (n == null || !Number.isFinite(Number(n))) return String(c);
  return `${c} (Kenya ${n})`;
}

function fmtCountyNatPct(c, n) {
  if (c == null || !Number.isFinite(Number(c))) return "—";
  if (n == null || !Number.isFinite(Number(n))) return `${c}%`;
  return `${c}% (Kenya ${n}%)`;
}

/**
 * Kenya county: KNBS census, KMHFL facility access, 2022 KDHS GF57 extract; primary action adds/removes list.
 */
const KenyaCountyDetailModal = ({ open, county, isSelected, onClose, onAddOrRemove }) => {
  const canon = typeof county === "string" ? county.trim() : "";
  const displayName = canon ? getKenyaCountyGoogleAdsDisplayName(canon) : "";
  const snap = useMemo(
    () => (canon ? getKenyaCountyCensusSnapshot(canon) : null),
    [canon]
  );
  const fac = useMemo(
    () => (canon ? getKenyaCountyFacilityAccessSnapshot(canon) : null),
    [canon]
  );
  const kdhs = useMemo(() => (canon ? getKenyaCountyKdhs2022Snapshot(canon) : null), [canon]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle component="div" sx={{ fontWeight: 700 }}>
        {displayName || canon || "County"}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Kenya county (Google Ads geo target). Figures are area- or survey-level, not individual patients.{" "}
          {KENYA_GOOGLE_ADS_COUNTY_LABEL_NOTE}
        </Typography>

        {!snap ? (
          <Typography variant="body2" color="text.secondary">
            No bundled census row for this county key.
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              KNBS census
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {KENYA_COUNTY_CENSUS_VINTAGE_LABEL}
              {typeof snap.censusYear === "number" ? ` (year ${snap.censusYear}).` : "."}
            </Typography>
            <StatRow label="Total population" value={fmtCount(snap.totalPopulation)} />
            <StatRow label="Male (% of total pop.)" value={fmtPct(snap.pctMale)} />
            <StatRow label="Female (% of total pop.)" value={fmtPct(snap.pctFemale)} />
            <StatRow label="Intersex (count)" value={fmtCount(snap.intersexCount)} />
            <StatRow label="Land area (km²)" value={fmtDecimal(snap.landAreaSqKm)} />
            <StatRow label="Population density (per km²)" value={fmtCount(snap.populationDensitySqKm)} />
          </Stack>
        )}

        {fac ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Facility access (KMHFL)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {KENYA_FACILITY_ACCESS_VINTAGE_LABEL}{" "}
              <Link href={KENYA_FACILITY_ACCESS_SOURCE_URL} target="_blank" rel="noopener noreferrer">
                HDX dataset
              </Link>
              .
            </Typography>
            <StatRow label="Operational facilities" value={fmtCount(fac.facilitiesOperational)} />
            <StatRow label="Facilities per 100k pop." value={fmtDecimal(fac.facilitiesPer100k)} />
            <StatRow label="Hospital / referral-type (bucket)" value={fmtCount(fac.hospitals)} />
            <StatRow label="PHC centre–type (bucket)" value={fmtCount(fac.healthCentres)} />
            <StatRow label="Dispensary / clinic-type (bucket)" value={fmtCount(fac.dispensariesClinics)} />
            <StatRow label="Public-sector-owned" value={fmtCount(fac.ownerPublicSector)} />
            <StatRow label="Private for-profit" value={fmtCount(fac.ownerPrivateForProfit)} />
            <StatRow label="Faith / NGO / community (bucket)" value={fmtCount(fac.ownerFaithNgoCommunity)} />
          </>
        ) : null}

        {kdhs ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              2022 KDHS (GF57 county fact sheet)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              {KENYA_KDHS_2022_VINTAGE_LABEL}{" "}
              <Link href={KENYA_KDHS_2022_PROGRAM_URL} target="_blank" rel="noopener noreferrer">
                DHS Program index
              </Link>
              {kdhs.factSheetUrl ? (
                <>
                  {" · "}
                  <Link href={String(kdhs.factSheetUrl)} target="_blank" rel="noopener noreferrer">
                    This county PDF
                  </Link>
                </>
              ) : null}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {KENYA_KDHS_2022_DATA_TYPE_NOTE}
            </Typography>
            <StatRow
              label="Under-5 mortality (per 1,000 live births)"
              value={fmtCountyNat(kdhs.under5MortalityPer1000County, kdhs.under5MortalityPer1000National)}
            />
            <StatRow
              label="Neonatal mortality (per 1,000)"
              value={fmtCountyNat(kdhs.neonatalMortalityPer1000County, kdhs.neonatalMortalityPer1000National)}
            />
            <StatRow
              label="Infant mortality (per 1,000)"
              value={fmtCountyNat(kdhs.infantMortalityPer1000County, kdhs.infantMortalityPer1000National)}
            />
            <StatRow
              label="Births with skilled provider (%)"
              value={fmtCountyNatPct(kdhs.skilledBirthPctCounty, kdhs.skilledBirthPctNational)}
            />
            <StatRow
              label="4+ antenatal visits (%)"
              value={fmtCountyNatPct(kdhs.anc4PlusPctCounty, kdhs.anc4PlusPctNational)}
            />
            <StatRow
              label="Full basic vaccination 12–23 months (%)"
              value={fmtCountyNatPct(
                kdhs.fullVaccination12to23mPctCounty,
                kdhs.fullVaccination12to23mPctNational
              )}
            />
          </>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <Button
          variant="contained"
          color={isSelected ? "secondary" : "primary"}
          disabled={!canon}
          onClick={() => {
            if (canon) onAddOrRemove(canon);
            onClose();
          }}
        >
          {isSelected ? "Remove from list" : "Add to list"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function StatRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Stack>
  );
}

export default KenyaCountyDetailModal;
